import React, { useState, useRef, useEffect } from "react";
import { useSelector } from "react-redux";
import ChartDataLabels from 'chartjs-plugin-datalabels';
import { MenuItem, Select, Switch, FormGroup, FormControlLabel, Typography } from "@material-ui/core";
import { Line } from 'react-chartjs-2';
import LegendItem from "./LegendItem.js";
import { makeStyles, useTheme } from "@material-ui/core/styles";
import "./Graph.css";

const useStyles = makeStyles(theme => ({
  root: {
    width: 50,
    height: 20,
    padding: 0,
  },
  graphSelector: {
    [theme.breakpoints.down('md')]: {
      width: "fit-content"
    },
  },
  textColor: {
    color: theme.palette.primary.main
  },
  switchBase: {
    padding: 1,
    '&$checked': {
      transform: 'translateX(30px)',
      color: "white",
      '& + $track': {
        backgroundColor: '#434d3e',
        opacity: 1,
        border: 'none',
      },
    },
    '&$focusVisible $thumb': {
      color: '#52d869',
      border: '6px solid #fff',
    },
  },
  compareLabel: {
    margin: 0,
    alignItems: "flex-end",
  },
  thumb: {
    width: 18,
    height: 18,
  },
  track: {
    borderRadius: 20 / 2,
    border: `1px solid grey`,
    backgroundColor: "grey",
    opacity: 1,
  },
  checked: {},
  focusVisible: {},
}));

export default function Graph(props) {
    const dashboard = useSelector((state) => state.dashboard);
    const classes = useStyles();
    const theme = useTheme();
    const ref = useRef();
    const [graphType, setGraphType] = useState('visits');
    const [showCompare, setShowCompare] = useState(false);
    
    // Data objects
    const rea_stat = dashboard?.data?.statistics?.rea_stat?.daily;
    const domain_stat = dashboard?.data?.statistics?.domainstat?.daily;
    const web_stat = dashboard?.data?.statistics?.website_stat?.daily;
    const fb_stat = dashboard?.data?.statistics?.fb_stat?.daily;
    const avg_stat = dashboard?.data?.statistics?.average_stat;
    
 
    const inspections = dashboard?.data?.inspections?.all_inspections || [];

    const mostRecentInspections = dashboard?.data?.inspections?.most_recent_inspections || [];
    
    // Average selector
    const [visibleGraph, setVisibleGraph] = useState("all");

    const [hiddenDatasets, setHiddenDatasets] = useState({
      rea: false,
      dom: false,
      fb: false,
      web: false
    });
    
    const max_ds_length = dashboard?.data?.statistics?.biggest_ds_length;
    const max_ds_daily = dashboard?.data?.statistics?.biggest_ds?.daily;
    const screen_multiplier = window.innerWidth <= 540 ? 12 : 40;
    // Different graph width is required to prevent line from jagging
    let graph_width;
    if (max_ds_length <= 14) {
      graph_width = '100%';
    } else if (max_ds_length <= 90) {
      graph_width = `${max_ds_length*screen_multiplier}px`;
    } else {
      graph_width = `${max_ds_length*screen_multiplier*0.6}px`;
    }
    let graph_height = "0px";
    if (window.innerHeight <= 600) {
      graph_height = "310px";
    } else if (window.innerHeight <=800) {
      graph_height = "360px";
    } else {
      graph_height = "480px";
    }
    let graph_legend = [];
    const drawLinePlugin = {
      afterDraw: (chart) => {
        // custom plugin to draw a vertical line on a graph
        if (chart?.tooltip?._active && chart?.tooltip?._active?.length) {
          // find coordinates of tooltip
          const activePoint = chart.tooltip._active[0];
          const { ctx } = chart;
          const { x } = activePoint.element;
          const topY = chart.scales.y.top;
          const bottomY = chart.scales.y.bottom;

          // draw vertical line
          ctx.save();
          ctx.beginPath();
          ctx.moveTo(x, topY);
          ctx.lineTo(x, bottomY);
          ctx.lineWidth = 1;
          ctx.strokeStyle = '#5a5a5a';
          ctx.stroke();
          ctx.restore();
        };
      }
    };

 
    const formatDate = (dateString) => {
      if (!dateString) return '';
      const parts = dateString.split('-');
      if (parts.length !== 3) return dateString;
      return `${parts[2]}/${parts[1]}/${parts[0]}`;
    };

    // datatypeMap helps to navigate and optimize value search for dashboard
    const datatypeMap = {
      visits: {
        rea: "pageView",
        dom: "totalListingViews",
        web: "full_display",
        fb: "clicks",
        avg: "page_views",
      },
      views: {
        rea: "searchResultsPageImpression",
        dom: "totalListingViews",
        web: "search_results",
        fb: "impressions",
        avg: "page_impressions",
      },
      email: {
        rea: "emailEnquiry",
        dom: "totalEnquiries",
        avg: "enquiries",
      },
      shares: {
        rea: "sendToFriend",
        dom: "totalEmailToFriend",
        avg: "shares",
      },
      saves: {
        rea: "listingSaved",
        dom: "totalShortlisted",
        avg: "saves"
      },
    };
    const totalsMap = {
      visits: [dashboard?.data?.statistics?.total_property_page_views, "Page Visits"],
      views: [dashboard?.data?.statistics?.total_property_exposure, "Total Exposure Views"],
      saves: [dashboard?.data?.statistics?.total_property_saves, "Saves"],
      shares: [dashboard?.data?.statistics?.total_property_shares, "Shares"],
      email: [dashboard?.data?.statistics?.total_property_enquiries, "Email Enquiries"],
    };
    const handleGraphChange = (event) => {
      setGraphType(event.target.value);
    };

    const handleCompareToggle = (event) => {
      const newCompareState = !showCompare;
      setShowCompare(newCompareState);
      
      if (newCompareState) {
        setHiddenDatasets({
          rea: false,
          dom: true,
          fb: true,
          web: true  // Mark Agency as hidden in compare mode
        });
        setVisibleGraph("rea");
      } else {
        setHiddenDatasets({
          rea: false,
          dom: false,
          fb: false,
          web: false
        });
        setVisibleGraph("all");
      }
    };

    const legendClick = (datasetName, showCompare, legendKey) => {
      if (showCompare) {
        if (legendKey === "rea" || legendKey === "dom") {
          setVisibleGraph(legendKey);
          
          // For REA selection, make web/Agency visible but mark as disabled
          if (legendKey === "rea") {
            setHiddenDatasets({
              rea: false,
              dom: true,
              fb: true,
              web: true   
            });
          } else if (legendKey === "dom") {
            setHiddenDatasets({
              rea: true,
              dom: false,
              fb: true,
              web: true  
            });
          }
        }
      } else {
 
        const newHiddenDatasets = {...hiddenDatasets};
        newHiddenDatasets[legendKey] = !hiddenDatasets[legendKey];
        setHiddenDatasets(newHiddenDatasets);
      }
    };

    function generateLabelArray() {
      const datasets = [rea_stat, domain_stat, fb_stat, web_stat];
      let dataset_index = datasets.reduce((p, c, i, a) => a[p]?.length > c?.length ? p : i, 0);
      let labels = [];
      // Getting labels from this array
      if (datasets[dataset_index] !== undefined) {
        labels = datasets[dataset_index]
          .map(a => a?.period || a?.eventDate)
          .filter(label => label !== undefined); 
      }
      return labels;
    }

    const generateDataset = (dataType, compare) => {
      /*
        Generates dataset for the graph
        Chartj.js will populate graph based on element indexes
        so if the graph should not always start from (0,0), the dataset must have null values
        Example (2 datasets):
        [1,2,3] - dataset A
        [1,2] - dataset B
        Will render both dataset from the beginning (dataset B will end earlier)
        [1,2,3] - dataset A
        [null,1,2] - dataset B
        Will render dataset B not from the beginning
      */
      let dataset = [];
      let dataObj = {};
      
      if (max_ds_daily) {
        max_ds_daily.forEach(function(key, index) {
          dataObj[key['period'] || key['eventDate']] = null;
        });
      }
      
      graph_legend.push(
        <LegendItem 
          key="web" 
          text="Agency" 
          datasetName="Agency" 
          visibleGraph={visibleGraph} 
          color={theme.palette.primary.main} 
          onClick={() => {legendClick("Agency", showCompare, "web")}} 
          compare={showCompare}
          disabled={showCompare || hiddenDatasets.web}  
          clickable={!showCompare}   
        />
      );
      graph_legend.push(
        <LegendItem 
          key="fb" 
          text="Facebook & Instagram" 
          datasetName="Facebook & Instagram" 
          visibleGraph={visibleGraph} 
          color="#0077fb" 
          onClick={() => {legendClick("Facebook & Instagram", showCompare, "fb")}} 
          compare={showCompare}
          disabled={hiddenDatasets.fb}
          clickable={!showCompare}
        />
      );
      graph_legend.push(
        <LegendItem 
          key="dom" 
          text="Domain" 
          datasetName="Domain" 
          visibleGraph={visibleGraph} 
          color="#13b000" 
          onClick={() => {legendClick("Domain", showCompare, "dom")}} 
          compare={showCompare}
          disabled={hiddenDatasets.dom}
          clickable={true}
        />
      );
      graph_legend.push(
        <LegendItem 
          key="rea" 
          text="REA" 
          datasetName="REA" 
          visibleGraph={visibleGraph} 
          color="#f92200" 
          onClick={() => {legendClick("REA", showCompare, "rea")}} 
          compare={showCompare}
          disabled={hiddenDatasets.rea}
          clickable={true}
        />
      );
      
      const shouldShowREA = !hiddenDatasets.rea;
      const shouldShowDomain = !hiddenDatasets.dom;
      const shouldShowFB = !hiddenDatasets.fb;
      const shouldShowWeb = !hiddenDatasets.web || (showCompare && visibleGraph === "rea");
      
      if (rea_stat?.length && shouldShowREA) {
        // Generating the list of values
        dataset.push({
          label: "REA",
          backgroundColor: '#f92200',
          borderColor: "#f92200",
          pointRadius: 0,
          fill: false,
          data: (function () {
            let rea_data = Object.assign({}, dataObj);
            rea_stat.forEach(element => {
              rea_data[element['period']] = element[datatypeMap[dataType].rea];
            });
            return Object.values(rea_data);
          }()),
          type: 'line',
          animation: {
            duration: 1500,
            easing: 'easeOutQuart'
          }
        });
      }
      
      if (domain_stat?.length && shouldShowDomain) {
        dataset.push({
          label: "Domain",
          backgroundColor: '#13b000',
          borderColor: "#13b000",
          pointRadius: 0,
          fill: false,
          data: (function () {
            let dom_data = Object.assign({}, dataObj);
            domain_stat.forEach(element => {
              dom_data[element['eventDate']] = element[datatypeMap[dataType].dom];
            });
            return Object.values(dom_data);
          }()),
          type: 'line',
          animation: {
            duration: 1500,
            easing: 'easeOutQuart'
          }
        });
      }
      
      if (fb_stat?.length && shouldShowFB) {
        dataset.push({
          label: "Facebook & Instagram",
          backgroundColor: "#0077fb",
          borderColor: "#0077fb",
          pointRadius: 0,
          fill: false,
          data: (function () {
            let fb_data = Object.assign({}, dataObj);
            fb_stat.forEach(element => {
              fb_data[element['period']] = element[datatypeMap[dataType].fb];
            });
            return Object.values(fb_data);
          }()),
          type: 'line',
          animation: {
            duration: 1500,
            easing: 'easeOutQuart'
          }
        });
      }
      
      if (web_stat?.length && shouldShowWeb) {
        dataset.push({
          label: "Agency",
          backgroundColor: theme.palette.primary.main,
          borderColor: theme.palette.primary.main,
          fill: false,
          data: (function () {
            let web_data = Object.assign({}, dataObj);
            web_stat.forEach(element => {
              web_data[element['period']] = element[datatypeMap[dataType].web];
            });
            return Object.values(web_data);
          }()),
          pointRadius: 0,
          type: 'line',
          animation: {
            duration: 1500,
            easing: 'easeOutQuart'
          }
        });
        
        // Only show attendees data if the graph type is "visits" (Page Visits dropdown option)
        if (graphType === 'visits' && inspections?.length > 0 && !compare) {
          const attendeeData = Object.assign({}, dataObj);
          
          // Map inspection dates to attendee counts directly from inspections data
          inspections.forEach(inspection => {
            if (inspection.inspection_date && attendeeData.hasOwnProperty(inspection.inspection_date)) {
              // Use total_attendees directly from API instead of calculating
              attendeeData[inspection.inspection_date] = inspection.total_attendees;
            }
          });
          
          // Check if there are any attendees to display
          if (Object.values(attendeeData).some(value => value > 0)) {
            dataset.push({
              label: "Attendees",
              backgroundColor: '#696969',
              data: Object.values(attendeeData),
              barThickness: 8,
              maxBarThickness: 20,
              type: 'bar',
              yAxisID: 'y1',
              order: 1,
              animation: {
                duration: 1200,
                easing: 'easeOutQuart'
              }
            });
          }
        }
      }
      
      if (compare && avg_stat && visibleGraph && avg_stat[visibleGraph]) {
        dataset.push({
          label: "Average",
          fill: true,
          backgroundColor: function(context) {
            const chart = context.chart;
            const { chartArea} = chart;
            if (!chartArea) {
              return null;
            }
            return `#1e0f144D`;
          },
          borderWidth: "0px",
          data: Object.values(avg_stat[visibleGraph]).map(value => value[datatypeMap[dataType].avg]),
          pointRadius: 0,
          type: 'line',
          animation: {
            duration: 1500,
            easing: 'easeOutQuart'
          }
        });
      }
      
      return dataset;
    };
    
    const getData = (compare) => {
      return {
        labels: generateLabelArray(),
        datasets: generateDataset(graphType, compare)
      };
    };
      
    const options = {
      responsive: true,
      maintainAspectRatio: false,
      interaction: {
        mode: 'index',
        axis: 'x',
        intersect: false,
      },
      elements: {
        point: {
          pointHitRadius: 20,
          pointHoverRadius: 4,
          pointHoverBackgroundColor: 'rgba(90, 90, 90, 0.8)',
          pointHoverBorderColor: "rgba(90, 90, 90, 0.8)"
        },
        line: {
          showLine: true,
          tension: 0.4,
          borderWidth: 5,
        },
        bar: {
          borderWidth: 0,
          borderRadius: 2,
        }
      },
      scales: {
        y: {
          grid: {
            borderDash: [20,10],
            drawBorder: false,
          },
          ticks: {
            display: false,
          }
        },
        y1: {
          position: 'right',
          beginAtZero: true,
          grid: {
            display: false,
          },
          ticks: {
            display: false
          }
        },
        x: {
          grid: {
            display: false,
            borderWidth: 2,
            borderColor: "black",
          },
          ticks: {
            callback:function(value, index, values){
              const label = this.getLabelForValue(value);
              if (!label) return ''; 
              let label_values = label.split('-');
              if (label_values.length === 3) {
                return label_values[2];  
              }
              return '';
            },
            align: "center",
            font: {
              size: 12,
              family: "filson-pro,sans-serif",
              lineHeight: 1,
            },
            maxRotation: 0,
            minRotation: 0,
          },
          offset: true, 
          afterFit: function(axis) {
            axis.paddingLeft = 20;
            axis.paddingRight = 20;
          }
        },
        monthScale: {
          type: 'category',
          position: 'bottom', 
          grid: {
            display: false,
            drawBorder: false,
          },
          ticks: {
            callback:function(value, index, values){
              const label = this.getLabelForValue(value);
              if (!label) return '';  
              
              let label_values = label.split('-');
              if (index === 0 || (label_values.length === 3 && label_values[2] === "01")) {
                let date = new Date(label_values[0], label_values[1]-1, label_values[2]);
                const formattedDate = date.toLocaleString("en-GB", {
                month: "short",
                year: "numeric",
                });
                return formattedDate;
              } else {
                return "";
              }
            },
            maxRotation: 0,
            minRotation: 0,
            padding: 0,
            align: "center", 
            autoSkipPadding: 0,
            font: {
              size: 12,
              family: "filson-pro,sans-serif",
              lineHeight: 1,
            }
          },
          offset: true 
        }
      },
      plugins: {
        datalabels: {
          display: false,
        },
        tooltip: {
          callbacks: {
            labelColor: function(tooltipItem, chart) {
                return {
                    borderColor: '#FFFFFF',
                    backgroundColor: '#FFFFFF'
                };
            },
            labelTextColor: function(tooltipItem, chart) {
              // Average dataset uses gradient so color need to be
              // configured manually
              if (tooltipItem.dataset.label === "Average") {
                return "#575757";
              }
              if (tooltipItem.dataset.label === "Attendees") {
                return "#696969";
              }
              return tooltipItem.dataset.backgroundColor;
            },
            label: function(context) {
              let label = context.dataset.label || '';
              if (label) {
                label += ': ';
              }
              if (context.parsed.y !== null) {
                if (label === "Attendees: ") {
                  label = "OFI - Attendees: ";
                }
                label += context.parsed.y;
              }
              else if (context.dataset.label === "Attendees") {
                label = "OFI - Attendees: 0";
              }
              return label;
            },
            title: function(tooltipItems) {
              if (tooltipItems.length === 0) return '';
              
              const item = tooltipItems[0];
              const label = item.label;
              if (!label) return '';
              
              return formatDate(label);
            }
          },
          position: 'nearest',
          bodySpacing: 5,
          displayColors: false,
          intersect: false, 
          mode: 'index', 
          caretSize: 0,
          caretPadding: 10,
          xAlign: 'center', 
          yAlign: 'bottom', 
          backgroundColor: 'rgba(255,255,255, 0.8)',
          titleColor: "#000000",
          titleFont: {
            size: "14px",
            family: "filson-soft,sans-serif",
            lineHeight: 1.2,
            weight: 400,
            style: "normal",
          },
          bodyFont: {
            size: "14px",
            family: "filson-soft,sans-serif",
            lineHeight: 1.2,
            weight: 400,
            style: "normal",
          }
        },
        legend: {
          display: false,
        }
      },
      animation: {
        duration: 1500,
        easing: 'easeOutQuart'
      },
      transitions: {
        active: {
          animation: {
            duration: 400
          }
        }
      },

      barPercentage: 0.9,
      categoryPercentage: 0.8,
      layout: {
        padding: {
          left: 10,
          right: 10
        }
      }
    };

 
    useEffect(() => {
      if (ref.current) {
        ref.current.update();
      }
    }, [hiddenDatasets, visibleGraph, showCompare, graphType]);

    return (
      <div className="graph-base">
        <div className="graph-general">
          <div className="graph-controls">
            <Typography className={classes.textColor} variant="h3">
              {totalsMap[graphType][0]} 
              <Typography className={classes.textColor} display={"inline"} variant="body1"> {totalsMap[graphType][1]}</Typography>
            </Typography>
            <FormGroup row>
              <FormControlLabel
                className={classes.compareLabel}
                value="end"
                control={
                  <Switch
                    checked={showCompare}
                    classes={{
                      root: classes.root,
                      switchBase: classes.switchBase,
                      thumb: classes.thumb,
                      track: classes.track,
                      checked: classes.checked,
                    }} 
                    color="primary"
                  />
                }
                label={<Typography variant="subtitle2" style={{padding: 0, lineHeight: 1, color: showCompare ? "#434d3e" : "gray"}}>Compare</Typography>}
                labelPlacement="bottom"
                onChange={handleCompareToggle}
              />
            </FormGroup>
          </div>
          <div className="secondary-controls">
            <Select
              disableUnderline
              className={classes.graphSelector}
              value={graphType}
              onChange={handleGraphChange}
              displayEmpty
              inputProps={{ 'aria-label': 'Without label' }}
            >
              <MenuItem selected classes={{selected: props.classes?.selected}} value="visits">Page Visits</MenuItem>
              <MenuItem selected classes={{selected: props.classes?.selected}} value="email">Email Enquiries</MenuItem>
              <MenuItem selected classes={{selected: props.classes?.selected}} value="saves">Saves</MenuItem>
              <MenuItem selected classes={{selected: props.classes?.selected}} value="shares">Shares</MenuItem>
            </Select>
            <div className="graph-legend">
              {graph_legend}
            </div>
          </div>
          <div className="canvas-wrap">
            <div className="canvas-big" style={{width: `${graph_width}`, height: `${graph_height}`}}>
              <Line 
                id="line_graph" 
                ref={ref} 
                data={getData(showCompare)} 
                options={options} 
                plugins={[ChartDataLabels, drawLinePlugin]}
              />
            </div>
          </div>
        </div>
      </div>
    );
}